import React, { useEffect } from "react";

export const SITE_NAME = "Beyond the Box";
export const SITE_ORIGIN = "https://beyondthebox.law";
// Branded default share image (Open Graph / Twitter).
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Title, description and share tags for one page. Used by the page itself in
// the browser and by the build (scripts/prerender.js) to write the same tags
// into that page's HTML file.
// fullTitle sets the whole title tag as written (for pages whose title carries
// the words people search for); otherwise " — Beyond the Box" is added.
export function buildMeta({ title, fullTitle: exactTitle, description, image, path, type = "website" }) {
  const fullTitle = exactTitle
    ? exactTitle
    : title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — The autonomy stack for law firm owners`;
  return {
    title: fullTitle,
    description,
    url: `${SITE_ORIGIN}${path === "/" ? "/" : path || ""}`,
    image: image || DEFAULT_OG_IMAGE,
    type,
  };
}

/**
 * Sets per-route document.title + Open Graph / Twitter meta tags.
 * In the browser this runs as an effect. When the build renders the page in
 * Node (scripts/ssr-render.js) there is no window, so the values are handed to
 * the build instead, which writes them into the page's HTML.
 */
export function useDocumentMeta({ title, fullTitle, description, image, path, type = "website" }) {
  if (typeof window === "undefined" && globalThis.__BTB_SSR_META__) {
    Object.assign(globalThis.__BTB_SSR_META__, buildMeta({ title, fullTitle, description, image, path, type }));
  }
  useEffect(() => {
    const m = buildMeta({ title, fullTitle, description, image, path: path || window.location.pathname, type });
    document.title = m.title;

    upsertMeta("name", "description", m.description);
    upsertMeta("property", "og:title", m.title);
    upsertMeta("property", "og:description", m.description);
    upsertMeta("property", "og:type", m.type);
    upsertMeta("property", "og:url", m.url);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:image", m.image);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", m.title);
    upsertMeta("name", "twitter:description", m.description);
    upsertMeta("name", "twitter:image", m.image);
    upsertCanonical(m.url);
  }, [title, fullTitle, description, image, path, type]);
}

/**
 * Structured data (JSON-LD) for search engines and answer engines. Rendered
 * inside the page, so the build puts it in the HTML and the browser keeps
 * exactly one copy.
 */
export function JsonLd({ data }) {
  const json = JSON.stringify(data).replace(/</g, "\\u003c");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
}

// Beyond the Box as an organisation, from what the site already says.
export const ORGANIZATION = {
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  url: `${SITE_ORIGIN}/`,
  description:
    "The autonomy stack for law firm owners who are done renting their firm from software vendors. Owner control over operations, compliance, people and growth.",
  parentOrganization: { "@type": "Organization", name: "Bonsai Law" },
};
